import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const interviewScheduling: FeatureContent = {
  slug: 'interview-scheduling',
  path: '/features/interview-scheduling',
  scorecardConnector:
    'This happens once a scorecard earns a yes: the candidate self-books a real slot, no email tennis.',
  audience: 'company',
  icon: 'Calendar',
  name: 'Smart Scheduling',
  stage: 4,
  navLabel: 'Interview Scheduling',
  navDescription: 'Candidates self-book real interview slots. No email tennis.',
  primaryKeyword: 'interview scheduling software',
  seo: {
    title: 'Interview Scheduling Software | Talenture.ai',
    description:
      'Interview scheduling software that lets candidates self-book real slots from your Google or Outlook calendar. Working-hours aware, conflict-checked, no login.',
    keywords: [
      'interview scheduling software',
      'automated interview scheduling',
      'self-scheduling interviews',
      'Google Calendar interview booking',
      'Outlook interview scheduling',
      'candidate self-booking',
    ],
    ogTitle: 'Interview Scheduling Software | Talenture.ai',
    ogDescription:
      'Candidates self-book real slots from your live calendar. Working-hours aware, conflict-checked, no login. From shortlist to booked interview, no email tennis.',
  },
  hero: {
    eyebrow: 'Stage 4 of the hiring mission · Coordinate interviews',
    whereThisFits:
      'Prepared, qualified candidates move straight into booked interviews, with their context attached.',
    h1: 'From shortlist to booked interview, no email tennis',
    painOpener: 'A dozen messages to book one interview.',
    subhead:
      'Candidates pick a real slot from your live calendar and book it themselves. Talenture handles availability, conflicts, and meeting links across Google and Outlook, so coordination stops eating your week.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is automated interview scheduling?',
    body: 'Automated interview scheduling lets candidates book a real interview slot directly from your calendar, without back-and-forth email. Talenture reads your live Google or Outlook availability, respects your working hours and buffers, and confirms the slot with a meeting link, so a booking takes minutes instead of days.',
  },
  sections: [
    {
      icon: 'CalendarCheck',
      title: 'Real availability, both calendars',
      body: 'Talenture connects to each interviewer\'s own calendar and offers only genuinely free slots, so candidates never book over a meeting you already have.',
      bullets: [
        'Google Calendar and Microsoft Outlook supported',
        'Teams links generated automatically for Outlook users',
        'Availability across a rolling 14-day window',
        'Busy times subtracted in real time',
      ],
    },
    {
      icon: 'Settings',
      title: 'Your rules, respected',
      body: 'The slots on offer follow your working hours, exclude weekends, and honour the buffers you set between meetings, so your day stays sane.',
      bullets: [
        'Working-hours aware',
        'Weekend exclusion',
        'Configurable buffers between interviews',
      ],
    },
    {
      icon: 'Link2',
      title: 'No login for candidates',
      body: 'Candidates book through a secure signed link, with no account to create. A final conflict check at confirmation prevents double bookings even if your calendar changed a moment earlier.',
    },
  ],
  visual: {
    component: 'SchedulingScreen',
    url: 'schedule.talenture.ai/meet/mira-n',
    caption: 'What the candidate sees: real slots from your live calendar, booked without an account.',
  },
  supportingVisuals: [
    {
      component: 'InterviewRecapScreen',
      url: 'app.talenture.ai/candidates/mira-n/interview-notes',
      heading: 'After the slot is booked',
      caption:
        'The notetaker joins the call and the interview comes back as a record: summary, insights, action items, transcript.',
    },
  ],
  howTo: {
    heading: 'How automated interview scheduling works',
    intro: 'Three steps from a connected calendar to a confirmed interview.',
    steps: [
      {
        title: 'Connect the interviewer calendar',
        body: 'Talenture reads each interviewer’s live Google Calendar or Microsoft Outlook availability and subtracts existing busy times.',
      },
      {
        title: 'Set the scheduling rules',
        body: 'Working hours, weekend exclusions, and buffers between meetings determine which genuine free slots can be offered.',
      },
      {
        title: 'Let the candidate book',
        body: 'The candidate chooses a slot through a secure link with no login. A final conflict check confirms the booking and creates the meeting link.',
      },
    ],
  },
  proofPoints: [
    { value: '2', label: 'calendar providers, Google and Outlook' },
    { value: '14-day', label: 'rolling availability window' },
    { value: 'No login', label: 'required for candidates to book' },
  ],
  faq: [
    {
      question: 'Which calendars does it work with?',
      answer:
        'Google Calendar and Microsoft Outlook, connected per interviewer. For Outlook users, Talenture also generates a Teams meeting link automatically when the interview is booked.',
    },
    {
      question: 'Do candidates need to create an account to book?',
      answer:
        'No. Candidates book through a secure signed link with no login. A conflict check runs again at the moment of confirmation, so even a last-second calendar change will not cause a double booking.',
    },
    {
      question: 'Can I control which times are offered?',
      answer:
        'Yes. Availability respects your working hours, excludes weekends, and honours the buffers you set between meetings. Candidates only ever see slots that are genuinely free and within your rules.',
    },
    {
      question: 'How does scheduling relate to pricing?',
      answer:
        'Talenture charges €299 on the first interview booked per application under the hiring-app model. You only pay when an interview actually happens, not for seats or subscriptions.',
    },
    {
      question: 'Does it handle different time zones?',
      answer:
        'Yes. Availability is presented so candidates and interviewers see consistent times, which keeps global scheduling clear and avoids the usual confusion over who meant which hour.',
    },
  ],
  relatedSlugs: ['ai-mock-interviews', 'ai-interview-notes', 'ai-candidate-matching'],
};
